import { useQuery } from "@tanstack/react-query";
import { dashApi, type OpsReadiness } from "@/lib/dashApi";
import { ErrorState, LoadingState, PageHeader, Panel, Stat } from "@/components/dash/ui";

const DR_LABELS: Record<string, string> = {
  remote_restore_verified: "DR ready (off-host restore verified)",
  off_host_replication_verified: "off-host verified (remote drill pending)",
  dr_partially_ready: "partially ready",
  local_restore_verified_only: "local restore verified only",
  dr_target_not_provisioned: "off-host DR not provisioned",
  dr_not_ready: "NOT ready", dr_evidence_insufficient: "evidence insufficient", unknown: "unknown",
};

export default function OperationalReadinessPage() {
  const q = useQuery({ queryKey: ["ops-readiness"], queryFn: dashApi.opsReadiness });
  const st = useQuery({ queryKey: ["ops-storage"], queryFn: dashApi.opsStorage });
  const dr = useQuery({ queryKey: ["ops-dr"], queryFn: dashApi.opsDrReadiness });
  if (q.isLoading) return <LoadingState />;
  if (q.isError) return <ErrorState error={q.error} />;
  const r = q.data as OpsReadiness;
  const slos = r.connector_slos as Record<string, number>;
  const backup = r.backup as {
    fresh?: boolean | null; last_backup_at?: string | null; location_class?: string;
    classification?: string; restore_classification?: string; off_host_protection?: boolean;
    automated?: boolean; next_scheduled_at?: string | null;
    last_restore_drill?: { status: string; started_at?: string | null } | null;
  };
  const auto = r.backup_automation;
  const ret = r.retention as { deletion_disabled_by_default?: boolean; unavailable?: boolean };
  const BACKUP_LABELS: Record<string, string> = {
    scheduled_verified: "scheduled · verified", scheduled_backup_stale: "scheduled · STALE",
    scheduled_backup_failed: "scheduled · FAILED", backup_verification_failed: "verification FAILED",
    manual_backup: "manual (unverified)", no_backup_recorded: "none recorded",
  };
  const DRILL_LABELS: Record<string, string> = {
    restore_drill_passed: "passed", restore_drill_failed: "FAILED", restore_drill_blocked: "blocked",
    restore_drill_not_run: "not yet run",
  };
  const cls = backup.classification ?? "unknown";
  const clsBad = ["scheduled_backup_failed", "backup_verification_failed", "no_backup_recorded"].includes(cls);
  const clsWarn = ["scheduled_backup_stale", "manual_backup"].includes(cls);
  const pc = r.period_close as { latest_finalised_period_end?: string | null; latest_finalised_type?: string | null };
  return (
    <div className="space-y-6">
      <PageHeader
        title="Operational Readiness"
        description="IRONDash's own production health at a glance. No secrets, credentials, container internals, raw logs or infrastructure paths are shown; missing signals read 'unknown' rather than guessed."
      />
      <div className={`rounded-lg border p-3 text-sm ${r.healthy ? "border-emerald-500/30 bg-emerald-500/5 text-emerald-200" : "border-rose-500/40 bg-rose-500/5 text-rose-200"}`}>
        {r.healthy ? "✓ No critical operational warnings." : `⚠ ${r.critical_warnings.length} critical warning(s):`}
        {!r.healthy && <ul className="mt-1 space-y-0.5 text-xs">{r.critical_warnings.map((w, i) => <li key={i}>• {w}</li>)}</ul>}
      </div>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Stat label="Schema version" value={r.schema_version ?? "—"} />
        <Stat label="App commit" value={r.app_commit} />
        <Stat label="Worker/scheduler" value={r.worker.status} sub={r.worker.minutes_since != null ? `${r.worker.minutes_since}m since activity` : undefined} />
        <Stat label="DB size" value={`${r.storage.db_size_mb} MB`} />
      </div>
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Panel title="Backup & restore">
          <div className={`mb-2 inline-block rounded px-2 py-0.5 text-[11px] ${clsBad ? "bg-rose-500/15 text-rose-200" : clsWarn ? "bg-amber-500/15 text-amber-200" : "bg-emerald-500/15 text-emerald-200"}`}>
            {BACKUP_LABELS[cls] ?? cls}
          </div>
          <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
            <F k="Last backup" v={backup.last_backup_at?.slice(0, 16).replace("T", " ") ?? "unknown"} />
            <F k="Automation" v={backup.automated ? "scheduled (host)" : auto.status} />
            <F k="Next scheduled" v={backup.next_scheduled_at?.slice(0, 16).replace("T", " ") ?? "—"} />
            <F k="Location" v={backup.off_host_protection ? "off-host" : "local-only"} />
            <F k="Restore drill" v={DRILL_LABELS[backup.restore_classification ?? ""] ?? "unknown"} />
            <F k="Integrity" v={auto.integrity_status ?? "unknown"} />
          </dl>
          {!backup.off_host_protection && backup.classification !== "no_backup_recorded" && (
            <p className="mt-2 text-[11px] text-amber-300/70">Local-only protection — no verified off-host / disaster-recovery copy yet.</p>
          )}
          {auto.blocker && <p className="mt-2 text-[11px] text-amber-300/70">{auto.blocker}</p>}
          <details className="mt-1 text-[11px] text-slate-500">
            <summary className="cursor-pointer">Backup operations checklist</summary>
            <ol className="mt-1 list-decimal space-y-0.5 pl-4">{auto.manual_checklist.map((c, i) => <li key={i}>{c}</li>)}</ol>
          </details>
        </Panel>
        <Panel title="Governance state">
          <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
            <F k="SLOs meeting" v={String(slos?.meeting ?? "—")} />
            <F k="SLOs breached" v={String(slos?.breached ?? "—")} />
            <F k="Retention deletion" v={ret?.unavailable ? "unknown" : ret?.deletion_disabled_by_default ? "disabled (safe)" : "ENABLED"} />
            <F k="Latest period close" v={pc?.latest_finalised_period_end ? `${pc.latest_finalised_type} ${pc.latest_finalised_period_end}` : "none"} />
          </dl>
        </Panel>
      </div>
      <Panel title="Disaster-recovery readiness" subtitle="Off-host protection, recovery-point (RPO) & recovery-time (RTO) evidence. Local-only is never shown as DR.">
        {dr.isLoading ? <LoadingState /> : dr.data && (() => {
          const d = dr.data;
          const drBad = ["dr_not_ready", "dr_target_not_provisioned", "dr_evidence_insufficient"].includes(d.dr_status);
          const drGood = d.dr_ready;
          return (
            <div className="space-y-3">
              <div className={`inline-block rounded px-2 py-0.5 text-[11px] ${drGood ? "bg-emerald-500/15 text-emerald-200" : drBad ? "bg-amber-500/15 text-amber-200" : "bg-sky-500/15 text-sky-200"}`}>
                {DR_LABELS[d.dr_status] ?? d.dr_status}
              </div>
              <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs sm:grid-cols-4">
                <F k="Local protection" v={d.local_protection.verified ? "verified" : (d.local_protection.classification ?? "—")} />
                <F k="Off-host protection" v={d.off_host_protection.provisioned ? (d.off_host_protection.verified ? "verified" : d.off_host_protection.off_host_class) : "not provisioned"} />
                <F k="RPO (local)" v={d.rpo.local_met ? "met (≤24h)" : "not met"} />
                <F k="RPO (off-host)" v={d.rpo.off_host_available ? (d.rpo.off_host_met ? "met" : "not met") : "unavailable"} />
                <F k="RTO evidence" v={d.rto.evidence_sufficient ? `${d.rto.local_drill_duration_seconds ?? "?"}s local drill` : "insufficient"} />
                <F k="Remote drill" v={d.remote_restore_drill.status} />
              </dl>
              <p className="text-[11px] text-amber-300/70">{d.rpo.reason}</p>
              {d.limitations.map((l, i) => <p key={i} className="text-[11px] text-slate-500">• {l}</p>)}
              <p className="text-[10px] text-slate-600">{d.rto.caveat}</p>
            </div>
          );
        })()}
      </Panel>
      <Panel title="Storage & growth" subtitle={st.data?.note}>
        {st.isLoading ? <LoadingState /> : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead><tr className="text-left text-[10px] uppercase text-slate-500"><th className="pb-1 pr-3">Table</th><th className="pb-1 pr-3">Rows (est.)</th><th className="pb-1">Size</th></tr></thead>
              <tbody>
                {(st.data?.largest_tables ?? []).map((t) => (
                  <tr key={t.table} className="border-t border-slate-800">
                    <td className="py-1 pr-3 text-slate-300">{t.table}</td>
                    <td className="py-1 pr-3 text-slate-400">{t.rows.toLocaleString()}</td>
                    <td className="py-1 text-slate-400">{t.size_mb} MB</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {(st.data?.warnings.length ?? 0) > 0 && (
          <ul className="mt-2 space-y-0.5 text-xs">{st.data!.warnings.map((w, i) => <li key={i} className={w.severity === "critical" ? "text-rose-300" : "text-amber-300"}>• {w.detail}</li>)}</ul>
        )}
      </Panel>
      <p className="text-[11px] text-slate-600">{r.safety}</p>
    </div>
  );
}

function F({ k, v }: { k: string; v: string }) {
  return <div><dt className="text-slate-500">{k}</dt><dd className="text-slate-300">{v}</dd></div>;
}
